import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import {
  catchError,
  map,
  Observable,
  Subject,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { RouterModule } from '@angular/router';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { User } from '../models/user.model';
import { Permission } from '../shared/constants/permissions';
import * as fromStore from '../store';
import { ChangePasswordDialogComponent } from './dialogs/change-password-dialog.component';
import { ConfirmDeactivateUserDialogComponent } from './dialogs/confirm-deactivate-user-dialog.component';
import { CreateEditUserDialogComponent } from './dialogs/create-edit-user-dialog.component';
import { OktaUserService } from './services/okta-user';
import { UserDialogData } from './user-dialog.model';

@Component({
  selector: 'app-manage-users',
  templateUrl: './manage-users.component.html',
  styleUrls: ['./manage-users.component.scss'],
  imports: [
    NgxPermissionsModule,
    CommonModule,
    MatTableModule,
    MatIconModule,
    MatDialogModule,
    MatTooltipModule,
    MatSortModule,
    MatSnackBarModule,
    RouterModule,
    MatPaginatorModule,
    MatButtonModule,
    MatOptionModule,
    MatFormFieldModule,
    MatSelectModule,
    MatMenuModule,
    MatFormFieldModule,
    FormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageUsersComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private store = inject(Store<fromStore.State>);

  private oktaUserService = inject(OktaUserService);

  private snackBar = inject(MatSnackBar);

  public permissionsService = inject(NgxPermissionsService);

  users$: Observable<User[]> = this.store.select(fromStore.getUsersWithOktaId);

  loggedInUser = signal<User>({} as User);

  dataSource: MatTableDataSource<User>;

  pageSizeOptions: number[] = [10, 25, 50];

  defaultPageSizeOption: number = 10;

  displayedColumns: string[] = [
    'Number',
    'UserInfo',
    'Username',
    'Role',
    'LastPortalLogin',
    'Actions',
  ];

  portalRoles$ = this.store.select(fromStore.getPortalRoles);

  totalSuperAdmins = signal(0);

  selectedRoleFilter: string = '';

  userSearchTerm: string = '';

  canManageUsers = signal(false);

  Permission = Permission;

  readonly dialog = inject(MatDialog);

  private readonly destroy$ = new Subject<void>();

  permissionList: Array<{ key: string; label: string }> = [
    { key: Permission.MANAGE_USERS, label: 'Manage Users' },
    {
      key: Permission.TICKETING_WRITE,
      label: 'Create Tickets',
    },
    { key: Permission.TICKETING_READ, label: 'View Tickets' },
    {
      key: Permission.BILLING_READ,
      label: 'View Invoices & Billing',
    },
    { key: Permission.ORDERS, label: 'View Orders' },
    { key: Permission.SERVICES, label: 'View Services' },
  ];

  async ngOnInit(): Promise<void> {
    this.store
      .select(fromStore.getContactsLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadContacts());
        }
      });

    this.store
      .select(fromStore.getPortalRolesLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadPortalRoles());
        }
      });

    this.users$.pipe(takeUntil(this.destroy$)).subscribe((users) => {
      this.dataSource = new MatTableDataSource(users);
      this.handleSorting();
      this.updateUserRoleCounts(users);

      // re-apply whatever the user currently has selected/typed
      this.applyUserSearchFilter();

      // Reset to first page so filtered results show from top
      this.paginator?.firstPage();
    });

    this.store
      .select(fromStore.getCurrentUser)
      .pipe(
        map((user) => {
          this.loggedInUser.set(user);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.canManageUsers.set(
      await this.permissionsService.hasPermission('Manage Users'),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  applyUserSearchFilter(): void {
    const term = this.userSearchTerm.trim().toLowerCase();

    this.dataSource.filterPredicate = (data: User, filter: string) => {
      const [searchTerm, roleFilter] = filter.split('||');

      const searchMatch =
        data.FirstName?.toLowerCase().includes(searchTerm) ||
        data.LastName?.toLowerCase().includes(searchTerm) ||
        data.Email?.toLowerCase().includes(searchTerm) ||
        data.OktaUsername__c?.toLowerCase().includes(searchTerm);

      const roleMatch = roleFilter
        ? data.Portal_Role__r?.Name === roleFilter
        : true;

      return searchMatch && roleMatch;
    };

    this.dataSource.filter = `${term}||${this.selectedRoleFilter}`;
  }

  handleSorting(): void {
    this.dataSource.sort = this.sort;
    // Sorting logic for nested properties
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'Role':
          return (item?.Portal_Role__r?.Name as string) || '';
        case 'UserInfo':
          return item.LastName || '';
        case 'LastPortalLogin':
          return (item?.Last_Portal_Login_At__c as string) || '';
        default:
          return item[property];
      }
    };

    this.dataSource.paginator = this.paginator;
  }

  onCreateEditUser(user?: User): void {
    const dialogRef = this.openCreateEditUserDialog(user);

    this.handleDialogResult(dialogRef, () => {
      this.reloadContacts();
    });
  }

  onDeactivateUser(user: User): void {
    const dialogRef = this.openDeactivateUserDialog(user);

    this.handleDialogResult(dialogRef, () => {
      this.deactivateUser(user);
    });
  }

  onResendInvite(user: User): void {
    this.oktaUserService
      .resendActivationEmail(user.OktaUsername__c)
      .pipe(
        takeUntil(this.destroy$),
        tap(() =>
          this.showSnackBar('Invite Resent Successfully', 'success-snackbar'),
        ),
        catchError(() => {
          this.showSnackBar(
            'Failed to Resend Invite. Please try again.',
            'error-snackbar',
          );
          return [];
        }),
      )
      .subscribe();
  }

  onChangePassword(): MatDialogRef<ChangePasswordDialogComponent, User> {
    return this.dialog.open(ChangePasswordDialogComponent, {
      disableClose: true,
      width: '400px',
      data: this.loggedInUser(),
    });
  }

  private openCreateEditUserDialog(
    user?: User,
  ): MatDialogRef<CreateEditUserDialogComponent, User> {
    return this.dialog.open(CreateEditUserDialogComponent, {
      disableClose: true,
      data: {
        ...user,
        AccountId: this.loggedInUser().AccountId,
        Account: { Name: this.loggedInUser().Account.Name },
        TotalSuperAdmins: this.totalSuperAdmins(),
      } as UserDialogData,
    });
  }

  private openDeactivateUserDialog(
    user: User,
  ): MatDialogRef<ConfirmDeactivateUserDialogComponent, User> {
    return this.dialog.open(ConfirmDeactivateUserDialogComponent, {
      width: '300px',
      data: user,
    });
  }

  private handleDialogResult(
    dialogRef: MatDialogRef<
      CreateEditUserDialogComponent | ConfirmDeactivateUserDialogComponent,
      User
    >,
    onSuccess: () => void,
  ): void {
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result != null) {
          onSuccess();
        }
      });
  }

  private deactivateUser(user: User): void {
    this.oktaUserService
      .deactivateUser({ login: user.OktaUsername__c, sfUserId: user.Id })
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.showSnackBar('User successfully removed!', 'success-snackbar');
          this.reloadContacts();
        }),
        catchError(() => {
          this.showSnackBar(
            'Error removing user. Please try again.',
            'error-snackbar',
          );
          return [];
        }),
      )
      .subscribe();
  }

  private reloadContacts(): void {
    this.store.dispatch(new fromStore.ClearContacts());
    this.store.dispatch(new fromStore.LoadContacts());
    // this.updateUserRoleCounts(this.dataSource.data);
  }

  private showSnackBar(message: string, panelClass: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 7000,
      panelClass: [panelClass],
    });
  }

  private updateUserRoleCounts(users: User[]): void {
    this.totalSuperAdmins.set(
      users.filter((user) => user.Portal_Role__r?.Name === 'Super Admin')
        .length,
    );
  }
}
